import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LogOut, FileText, Mail, Calendar } from "lucide-react";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [polizze, setPolizze] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, r] = await Promise.all([api.get("/portfolio"), api.get("/contact/mine")]);
        setPolizze(p.data.items || []);
        setRichieste(r.data.items || []);
      } catch (_e) {
        toast.error("Errore caricamento dati");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const doLogout = async () => {
    await logout();
    toast.success("Disconnesso");
    nav("/");
  };

  const totalePremi = polizze.reduce((s, p) => s + (p.premio_annuo || 0), 0);

  return (
    <div data-testid="page-dashboard" className="pt-28 pb-32">
      <section className="container-x py-12 grid md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-8">
          <span className="overline">Area Riservata</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-tight">
            Bentornato, <span className="italic text-primary">{user?.name?.split(" ")[0] || "cliente"}</span>.
          </h1>
          <p className="mt-3 text-muted-foreground">{user?.email}{user?.company ? ` · ${user.company}` : ""}</p>
        </div>
        <div className="md:col-span-4 md:justify-self-end">
          <button onClick={doLogout} data-testid="dashboard-logout" className="btn-outline">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </section>

      <section className="container-x mt-12 grid sm:grid-cols-3 gap-px bg-foreground/10 border border-foreground/10">
        <div className="bg-background p-8" data-testid="kpi-polizze">
          <p className="overline">Polizze attive</p>
          <p className="mt-4 font-display text-5xl">{polizze.length}</p>
        </div>
        <div className="bg-background p-8" data-testid="kpi-premio">
          <p className="overline">Premio annuo totale</p>
          <p className="mt-4 font-display text-5xl">
            € {totalePremi.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-background p-8" data-testid="kpi-richieste">
          <p className="overline">Richieste inviate</p>
          <p className="mt-4 font-display text-5xl">{richieste.length}</p>
        </div>
      </section>

      <section className="container-x mt-20 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-3xl flex items-center gap-3">
              <FileText className="h-6 w-6 text-primary" /> Le tue polizze
            </h2>
          </div>
          {loading ? (
            <p className="text-muted-foreground">Caricamento...</p>
          ) : polizze.length === 0 ? (
            <p className="text-muted-foreground">Nessuna polizza attiva.</p>
          ) : (
            <ul className="border-t border-foreground/10" data-testid="polizze-list">
              {polizze.map((p) => (
                <li key={p.id} className="grid grid-cols-12 gap-4 py-6 border-b border-foreground/10" data-testid={`polizza-${p.id}`}>
                  <div className="col-span-12 sm:col-span-7">
                    <p className="font-display text-xl">{p.ramo}</p>
                    <p className="text-sm text-muted-foreground mt-1">{p.compagnia} · {p.id}</p>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground">Premio</p>
                    <p className="mt-1 font-medium">€ {p.premio_annuo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="col-span-6 sm:col-span-2 text-right">
                    <p className="text-xs uppercase tracking-[0.24em] text-muted-foreground flex items-center gap-1 justify-end">
                      <Calendar className="h-3 w-3" /> Scad.
                    </p>
                    <p className="mt-1 text-sm">{p.scadenza}</p>
                    <p className={`mt-1 text-xs ${p.stato === "Attiva" ? "text-primary" : "text-foreground/70"}`}>{p.stato}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-5">
          <h2 className="font-display text-3xl flex items-center gap-3 mb-6">
            <Mail className="h-6 w-6 text-primary" /> Richieste recenti
          </h2>
          {loading ? (
            <p className="text-muted-foreground">Caricamento...</p>
          ) : richieste.length === 0 ? (
            <div className="border-t border-foreground/10 pt-6 text-muted-foreground">
              Non hai ancora inviato richieste. <br />
              <button className="mt-3 underline-link text-foreground" onClick={() => nav("/contatti")} data-testid="empty-contact-cta">
                Apri una richiesta
              </button>
            </div>
          ) : (
            <ul className="border-t border-foreground/10" data-testid="richieste-list">
              {richieste.map((r) => (
                <li key={r.id} className="py-5 border-b border-foreground/10">
                  <p className="font-medium">{r.oggetto}</p>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.messaggio}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.24em] text-primary">{r.status}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
